"use client"

import type React from "react"
import { useState } from "react"
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Button,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    MenuItem,
    Box,
} from "@mui/material"
import { IconX, IconBrandWhatsapp } from "@tabler/icons-react"

interface CustomOrderModalProps {
    onClose: () => void
}

const CustomOrderModal: React.FC<CustomOrderModalProps> = ({ onClose }) => {
    const [name, setName] = useState("")
    const [phone, setPhone] = useState("")
    const [size, setSize] = useState("")
    const [customSize, setCustomSize] = useState("")
    const [hasImage, setHasImage] = useState("")
    const [imageUrl, setImageUrl] = useState("")
    const [theme, setTheme] = useState("")
    const [spotifyLink, setSpotifyLink] = useState("")

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        let sizeText = size
        if (size === "custom") {
            sizeText = `Medida personalizada: ${customSize}`
        }

        let imageText = ""
        if (hasImage === "yes") {
            imageText = `Ya tengo la imagen: ${imageUrl}`
        } else if (hasImage === "no") {
            imageText = `Necesito ayuda para buscar imágenes. Temática: ${theme}`
        }

        const spotifyText = spotifyLink ? `Enlace de Spotify/video: ${spotifyLink}` : ""

        const message = encodeURIComponent(
            `Hola, quiero solicitar un cuadrito personalizado.\n\n` +
            `Nombre: ${name}\n` +
            `Teléfono: ${phone}\n` +
            `Tamaño: ${sizeText}\n` +
            `${imageText}\n` +
            `${spotifyText}`,
        )

        window.open(`https://wa.me/59174318810?text=${message}`, "_blank")
        onClose()
    }

    return (
        <Dialog
            open={true}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
                    maxHeight: "90vh",
                },
            }}
        >
            <DialogTitle
                sx={{
                    textAlign: "center",
                    color: "#0d6336",
                    fontWeight: 600,
                    pt: 4,
                    pb: 2,
                }}
            >
                Solicitar Cuadrito Personalizado
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "white",
                        backgroundColor: "#3c2a20",
                        "&:hover": {
                            backgroundColor: "#0d6336",
                        },
                    }}
                >
                    <IconX size={20} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 4, pb: 4 }}>
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="name"
                        label="Nombre"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="phone"
                        label="Número de teléfono"
                        name="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        sx={{ mb: 2 }}
                    />

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <FormLabel id="size-label">Tamaño</FormLabel>
                        <TextField select required value={size} onChange={(e) => setSize(e.target.value)} fullWidth>
                            <MenuItem value="">Selecciona un tamaño</MenuItem>
                            <MenuItem value="15x15 cm - 25 Bs.">15x15 cm - 25 Bs. (El que viste en el stand)</MenuItem>
                            <MenuItem value="20x20 cm - 35 Bs.">20x20 cm - 35 Bs.</MenuItem>
                            <MenuItem value="30x30 cm - 70 Bs.">30x30 cm - 70 Bs.</MenuItem>
                            <MenuItem value="45x45 cm - 150 Bs.">45x45 cm - 150 Bs.</MenuItem>
                            <MenuItem value="45x60 cm - 200 Bs.">45x60 cm - 200 Bs.</MenuItem>
                            <MenuItem value="60x60 cm - 250 Bs.">60x60 cm - 250 Bs.</MenuItem>
                            <MenuItem value="60x90 cm - 350 Bs.">60x90 cm - 350 Bs.</MenuItem>
                            <MenuItem value="custom">Medida personalizada</MenuItem>
                        </TextField>
                    </FormControl>

                    {size === "custom" && (
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="customSize"
                            label="Especifica la medida personalizada"
                            name="customSize"
                            value={customSize}
                            onChange={(e) => setCustomSize(e.target.value)}
                            placeholder="Ej: 40x50 cm"
                            helperText="Cotizaremos el precio según el tamaño y te avisaremos."
                            sx={{ mb: 2 }}
                        />
                    )}

                    <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
                        <FormLabel component="legend">¿Ya tienes la imagen que quieres?</FormLabel>
                        <RadioGroup value={hasImage} onChange={(e) => setHasImage(e.target.value)}>
                            <FormControlLabel value="yes" control={<Radio />} label="Sí, ya tengo la imagen" />
                            <FormControlLabel value="no" control={<Radio />} label="No, necesito ayuda para buscarla" />
                        </RadioGroup>
                    </FormControl>

                    {hasImage === "yes" && (
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="imageUrl"
                            label="URL de la imagen o descripción"
                            name="imageUrl"
                            value={imageUrl}
                            onChange={(e) => setImageUrl(e.target.value)}
                            placeholder="Pega el enlace de la imagen o describe dónde la enviarás"
                            sx={{ mb: 2 }}
                        />
                    )}

                    {hasImage === "no" && (
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="theme"
                            label="Temática o idea para la imagen"
                            name="theme"
                            value={theme}
                            onChange={(e) => setTheme(e.target.value)}
                            placeholder="Describe qué tipo de imagen estás buscando"
                            multiline
                            rows={4}
                            sx={{ mb: 2 }}
                        />
                    )}

                    <TextField
                        margin="normal"
                        fullWidth
                        id="spotifyLink"
                        label="Enlace de Spotify o video (opcional)"
                        name="spotifyLink"
                        value={spotifyLink}
                        onChange={(e) => setSpotifyLink(e.target.value)}
                        placeholder="Ej: https://open.spotify.com/track/..."
                        helperText="Si deseas incluir un código QR con una canción o video."
                        sx={{ mb: 3 }}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        startIcon={<IconBrandWhatsapp size={24} />}
                        sx={{
                            backgroundColor: "#25D366",
                            "&:hover": {
                                backgroundColor: "#128C7E",
                            },
                            py: 1.5,
                        }}
                    >
                        Enviar solicitud
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default CustomOrderModal

